import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useState, useEffect } from "react";
import { Button, Loading, Modal, Typography } from "@/components/atoms";
import { AddCardForm } from "@/components/organisms/Onboarding/AddCardForm";
import { PaymentMethodCard } from "@/components/organisms/Onboarding/PaymentMethodCard";
import { SubscriptionGroup } from "@/components/organisms/PlanSelection";
import { CannotDowngradeModal, ReviewPlanChangesModal, ConfirmDowngradeModal } from "@/components/organisms/SubscriptionPlan/modals";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useGetFleetDriversQuery } from "@/redux/apis/driver/driverApi";
import { useSubscriptionPlanLimits } from "@/hooks";
import { getDisabledPlanInfo } from "@/redux/apis/subscription/subscriptionLimits";
import {
  useCreateSubscriptionMutation,
  useGetSubscriptionPlansQuery,
  useLazyGetStripeCustomerQuery,
  useLazyGetPaymentMethodsQuery,
} from "@/redux/apis/subscription/subscriptionPlansApi";
import { SubscriptionPlan } from "@/redux/apis/subscription/types";
import { useGetVehiclesQuery } from "@/redux/apis/vehicle/vehicleApi";
import { useAppSelector } from "@/redux/hooks";
import { fleetDriversPageSelector } from "@/redux/slices/driver/selectors";
import { activeOperatorSelector } from "@/redux/slices/operator/selectors";
import { addToast } from "@/utils/addToast";
import { formatCurrency } from "@/utils/formatCurrency";

interface PlanSelectionModalProps {
  open: boolean;
  onClose?: () => void;
  variant: "activate" | "trial-ended" | "subscription-ended";
  planName?: string;
  currentSubscriptionId?: string;
  preSelectedPlanId?: string;
}

const MODAL_CONTENT = {
  activate: {
    title: "Activate your {planName} plan",
    description: "Begin billing today on your {planName} plan, or switch to a different plan before you start.",
  },
  "trial-ended": {
    title: "Your {planName} plan trial has ended, choose a plan to continue",
    description: "Activate your {planName} plan to continue, or select a different plan.",
  },
  "subscription-ended": {
    title: "Choose a plan to continue",
    description: "Your trial and subscription have ended. Select a plan below to regain access to the platform.",
  },
};

export const PlanSelectionModal = ({ open, onClose, variant, planName = "Enterprise", currentSubscriptionId, preSelectedPlanId }: PlanSelectionModalProps) => {
  const { getPlanLimits } = useSubscriptionPlanLimits();
  const operator = useAppSelector(activeOperatorSelector);
  const [selectedPlanId, setSelectedPlanId] = useState(preSelectedPlanId || currentSubscriptionId || "");
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState("");
  const [showAddCard, setShowAddCard] = useState(false);
  const [addCardKey, setAddCardKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cannotDowngradeModalOpen, setCannotDowngradeModalOpen] = useState(false);
  const [downgradeModalData, setDowngradeModalData] = useState({
    planName: "",
    currentVehicles: 0,
    allowedVehicles: 0,
    currentDrivers: 0,
    allowedDrivers: 0,
    exceedsVehicles: false,
    exceedsDrivers: false,
  });

  // Downgrade/Upgrade flow modals state
  const [reviewPlanChangesModalOpen, setReviewPlanChangesModalOpen] = useState(false);
  const [confirmDowngradeModalOpen, setConfirmDowngradeModalOpen] = useState(false);
  const [targetPlanForModal, setTargetPlanForModal] = useState<SubscriptionPlan | null>(null);

  // Stripe-related state
  const [stripeCustomerId, setStripeCustomerId] = useState<string>("");
  const [loadingStripeData, setLoadingStripeData] = useState(true);

  // Modal is non-closable for trial-ended and subscription-ended variants
  const isClosable = variant === "activate";

  // Fetch subscription plans
  const { data: plansData, isLoading: isLoadingPlans } = useGetSubscriptionPlansQuery();

  // Get current vehicle count — only migrated vehicles count toward limits
  const { data: vehiclesData, isLoading: isLoadingVehicles } = useGetVehiclesQuery(
    {
      owner_uuid: operator?.id ?? "",
      needs_type_migration: false,
      page_size: "1",
      page: "1",
    },
    {
      skip: !operator?.id,
    }
  );

  // Get current driver count
  const { isLoading: isLoadingDrivers } = useGetFleetDriversQuery(
    {
      provider_uuid: operator?.id ?? "",
      page: "1",
      page_size: "1",
    },
    {
      skip: !operator?.id,
    }
  );

  const vehicleCount = vehiclesData?.page.total ?? 0;
  const driverCount = useAppSelector(fleetDriversPageSelector).total ?? 0;
  const isLoadingCounts = isLoadingVehicles || isLoadingDrivers;

  // Lazy queries for Stripe data
  const [getStripeCustomer] = useLazyGetStripeCustomerQuery();
  const [getPaymentMethods, { data: paymentMethodsData, isLoading: isLoadingPaymentMethods, isFetching: isFetchingPaymentMethods }] =
    useLazyGetPaymentMethodsQuery();

  // Mutation for creating subscription
  const [createSubscription] = useCreateSubscriptionMutation();

  const content = MODAL_CONTENT[variant];
  const title = content.title.replace("{planName}", planName);
  const description = content.description.replace(/{planName}/g, planName);

  // Set the selected plan when modal opens — prefer preSelectedPlanId, fallback to currentSubscriptionId
  useEffect(() => {
    if (open) {
      if (preSelectedPlanId) {
        setSelectedPlanId(preSelectedPlanId);
      } else if (currentSubscriptionId) {
        setSelectedPlanId(currentSubscriptionId);
      }
    }
  }, [open, currentSubscriptionId, preSelectedPlanId]);

  // Load Stripe customer and payment methods when modal opens
  useEffect(() => {
    if (!open) return;

    const loadStripeData = async () => {
      if (!operator?.id) return;

      try {
        setLoadingStripeData(true);

        // Get Stripe customer ID
        const customerResult = await getStripeCustomer({ providerId: operator.id }).unwrap();

        if (customerResult.stripeCustomerId) {
          setStripeCustomerId(customerResult.stripeCustomerId);

          // Load payment methods
          await getPaymentMethods({ customerId: customerResult.stripeCustomerId });
        }
      } catch {
        console.error("Failed to load Stripe data");
      } finally {
        setLoadingStripeData(false);
      }
    };

    loadStripeData();
  }, [open, operator?.id, getStripeCustomer, getPaymentMethods]);

  const handleCardAdded = async (paymentMethodId: string) => {
    setShowAddCard(false);
    setSelectedPaymentMethodId(paymentMethodId);

    // Reload payment methods
    if (stripeCustomerId) {
      await getPaymentMethods({ customerId: stripeCustomerId });
    }
  };

  const handleRefreshPaymentMethods = async () => {
    if (stripeCustomerId) {
      await getPaymentMethods({ customerId: stripeCustomerId });
    }
  };

  // Determine if the selected plan is a downgrade from the current plan
  const isDowngrade = (targetPlanId: string): boolean => {
    if (!currentSubscriptionId || currentSubscriptionId === targetPlanId) return false;
    const currentPlanData = subscriptionPlans.find((s) => s.subscriptionId === currentSubscriptionId);
    const targetPlanData = subscriptionPlans.find((s) => s.subscriptionId === targetPlanId);
    if (!currentPlanData || !targetPlanData) return false;
    return targetPlanData.subscriptionFee < currentPlanData.subscriptionFee;
  };

  const handleSubmit = async () => {
    if (!selectedPlanId || !selectedPaymentMethodId) return;
    if (!operator?.id) {
      addToast("danger", "Operator not found");
      return;
    }

    // Wait for vehicle and driver counts to load before validating
    if (isLoadingCounts) {
      addToast("info", "Loading your account information...");
      return;
    }

    // Get the target plan to check limits
    const targetPlan = subscriptionPlans.find((s) => s.subscriptionId === selectedPlanId);
    if (!targetPlan) {
      addToast("danger", "Target subscription plan not found");
      return;
    }

    // Only validate if user has an active subscription and is switching to a different plan
    if (currentSubscriptionId && currentSubscriptionId !== selectedPlanId) {
      // Get plan limits using the slug from the subscription data
      const targetLimits = getPlanLimits(targetPlan.slug);

      // Check if current counts exceed target plan limits
      const exceedsVehicles = vehicleCount > targetLimits.maxVehicles;
      const exceedsDrivers = driverCount > targetLimits.maxDrivers;

      if (exceedsVehicles || exceedsDrivers) {
        // Show cannot downgrade modal
        setDowngradeModalData({
          planName: targetPlan.subscriptionName,
          currentVehicles: vehicleCount,
          allowedVehicles: targetLimits.maxVehicles,
          currentDrivers: driverCount,
          allowedDrivers: targetLimits.maxDrivers,
          exceedsVehicles,
          exceedsDrivers,
        });
        setCannotDowngradeModalOpen(true);
        return;
      }

      // If switching plans, show review modal for downgrades or proceed directly for upgrades
      setTargetPlanForModal(targetPlan);
      if (isDowngrade(selectedPlanId)) {
        // Show review plan changes modal for downgrades
        setReviewPlanChangesModalOpen(true);
        return;
      } else {
        // For upgrades, proceed directly to subscription
        await proceedWithSubscription(targetPlan);
        return;
      }
    }

    // No current subscription or same plan selected - proceed directly
    await proceedWithSubscription(targetPlan);
  };

  // Handle the actual subscription creation
  const proceedWithSubscription = async (targetPlan: SubscriptionPlan) => {
    if (!operator?.id) return;

    setIsSubmitting(true);

    try {
      // Call the subscription API
      await createSubscription({
        providerId: operator.id,
        subscriptionPlanId: targetPlan.subscriptionId,
        stripePaymentMethodId: selectedPaymentMethodId,
      }).unwrap();

      // Set flag to show subscription active overlay after reload
      sessionStorage.setItem("showSubscriptionActiveOverlay", "true");
      // Close the modal before reloading
      onClose?.();
      window.location.reload();
    } catch (error) {
      getErrorMessages(error as FetchBaseQueryError | SerializedError).forEach((m) => addToast("danger", m));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle review plan changes modal continue (proceed to confirm downgrade)
  const handleReviewPlanChangesContinue = () => {
    setReviewPlanChangesModalOpen(false);
    setConfirmDowngradeModalOpen(true);
  };

  // Handle confirm downgrade
  const handleConfirmDowngrade = async () => {
    if (!targetPlanForModal) return;
    setConfirmDowngradeModalOpen(false);
    await proceedWithSubscription(targetPlanForModal);
  };

  // Get subscription plans
  const subscriptionPlans = plansData?.subscriptions ?? [];
  const paymentMethods = paymentMethodsData ?? [];

  // Get selected subscription plan
  const selectedPlan = subscriptionPlans.find((plan) => plan.subscriptionId === selectedPlanId);

  // For trial-ended and subscription-ended variants, check which plans exceed limits
  // This prevents users from selecting plans they can't use
  const shouldDisablePlansExceedingLimits = variant === "trial-ended" || variant === "subscription-ended";

  const getPlanDisabledInfo = (plan: SubscriptionPlan): { disabled: boolean; reason?: string } => {
    // Check if plan is temporarily disabled (e.g., Solo)
    const disabledPlanInfo = getDisabledPlanInfo(plan.slug);
    if (disabledPlanInfo.disabled) {
      return disabledPlanInfo;
    }

    // Check if plan exceeds limits (only for trial-ended and subscription-ended variants)
    if (!shouldDisablePlansExceedingLimits) return { disabled: false };

    const limits = getPlanLimits(plan.slug);
    const exceedsVehicles = vehicleCount > limits.maxVehicles;
    const exceedsDrivers = driverCount > limits.maxDrivers;

    if (exceedsVehicles && exceedsDrivers) {
      return {
        disabled: true,
        reason: `Exceeds vehicle limit (${vehicleCount}/${limits.maxVehicles}) and driver limit (${driverCount}/${limits.maxDrivers})`,
      };
    } else if (exceedsVehicles) {
      return {
        disabled: true,
        reason: `Exceeds vehicle limit (${vehicleCount}/${limits.maxVehicles})`,
      };
    } else if (exceedsDrivers) {
      return {
        disabled: true,
        reason: `Exceeds driver limit (${driverCount}/${limits.maxDrivers})`,
      };
    }

    return { disabled: false };
  };

  // Show loading while fetching plans or Stripe data
  const isLoadingData = isLoadingPlans || loadingStripeData;

  return (
    <Modal open={open} onClose={isClosable ? onClose : undefined} variant="web-large" className="max-w-[912px]">
      {(isSubmitting || isLoadingData) && <Loading className="rounded-lg" />}

      <div className="mb-6 flex flex-col">
        <Typography variant="h3">{title}</Typography>
        <Typography variant="paragraph" className="mt-2 text-neutral-dark-gray">
          {description}
        </Typography>
      </div>

      {/* Two Column Layout - 2/3 for plans, 1/3 for payment */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        {/* Left Column - Subscription Plan Selection (2/3 width) */}
        <div className="lg:col-span-2">
          <Typography variant="title" className="mb-4">
            Subscription Plans
          </Typography>
          <SubscriptionGroup value={selectedPlanId} onChange={setSelectedPlanId}>
            {subscriptionPlans.map((plan) => {
              const disabledInfo = getPlanDisabledInfo(plan);
              return (
                <SubscriptionGroup.Option
                  key={plan.subscriptionId}
                  value={plan.subscriptionId}
                  title={plan.subscriptionName}
                  description={plan.subscriptionDescription ?? ""}
                  pricing={`${formatCurrency(plan.subscriptionFee, 0, "AUD")} /month`}
                  iconSvg={plan.iconSvg ?? undefined}
                  disabled={disabledInfo.disabled}
                  disabledReason={disabledInfo.reason}
                />
              );
            })}
          </SubscriptionGroup>
        </div>

        {/* Right Column - Payment Method Section (1/3 width) */}
        <div className="flex flex-col gap-6">
          {stripeCustomerId ? (
            <>
              <div>
                <div className="mb-4 flex items-center justify-between">
                  <Typography variant="title">Payment Method</Typography>
                  {!showAddCard && (
                    <Button
                      variant="tertiary-light"
                      size="sm"
                      type="button"
                      onClick={handleRefreshPaymentMethods}
                      disabled={isFetchingPaymentMethods}
                    >
                      {isFetchingPaymentMethods ? "Refreshing..." : "Refresh"}
                    </Button>
                  )}
                </div>

                {isLoadingPaymentMethods ? (
                  <div className="border-neutral-light-gray relative min-h-[150px] rounded-lg border">
                    <Loading className="rounded-lg" />
                  </div>
                ) : (
                  <div className="flex-1 space-y-3">
                    {/* Existing Payment Methods - Scrollable */}
                    {paymentMethods.length > 0 && !showAddCard && (
                      <div className="relative">
                        {isFetchingPaymentMethods && <Loading className="rounded-lg" />}
                        <div className="-m-1 grid max-h-[240px] gap-2 overflow-y-auto p-1">
                          {paymentMethods.map((pm) => (
                            <PaymentMethodCard
                              key={pm.paymentMethodId}
                              paymentMethod={pm}
                              isSelected={selectedPaymentMethodId === pm.paymentMethodId}
                              onSelect={(id) => setSelectedPaymentMethodId(id)}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Add New Card Section */}
                    {showAddCard ? (
                      <div className="bg-primary/5 flex flex-col gap-2 rounded-lg border border-primary p-4">
                        <Typography variant="action">Add New Card</Typography>
                        <AddCardForm
                          key={addCardKey}
                          providerId={operator?.id || ""}
                          onCardAdded={handleCardAdded}
                          onCancel={() => {
                            setShowAddCard(false);
                            setAddCardKey((prev) => prev + 1);
                          }}
                        />
                      </div>
                    ) : (
                      <Button
                        variant="secondary"
                        size="lg"
                        type="button"
                        className="w-full"
                        onClick={() => setShowAddCard(true)}
                        disabled={isFetchingPaymentMethods}
                      >
                        + Add New Card
                      </Button>
                    )}

                    {paymentMethods.length === 0 && !showAddCard && !isFetchingPaymentMethods && (
                      <Typography variant="paragraph" className="py-2 text-center text-neutral-dark-gray">
                        No payment methods found. Add a new card to continue.
                      </Typography>
                    )}

                    {paymentMethods.length === 0 && !showAddCard && isFetchingPaymentMethods && (
                      <div className="relative min-h-[100px]">
                        <Loading className="rounded-lg" />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Pricing Summary - only show when plan is selected */}
              {selectedPlan && (
                <div className="space-y-4">
                  <div className="space-y-4 rounded-lg border border-neutral-mid-gray bg-neutral-gray p-4">
                    {/* One-time setup fee */}
                    <div>
                      <div className="flex justify-between">
                        <Typography variant="paragraph" className="text-neutral-dark-gray">
                          One-time setup fee:
                        </Typography>
                        <Typography variant="paragraph">{formatCurrency(selectedPlan.setupFee || 0, 0, "AUD")}</Typography>
                      </div>
                    </div>

                    {/* Plan subscription */}
                    <div className="border-t border-neutral-mid-gray pt-4">
                      <div className="flex items-center justify-between">
                        <div className="flex flex-col gap-1">
                          <Typography variant="paragraph" className="text-neutral-dark-gray">
                            {selectedPlan.subscriptionName} Plan:
                          </Typography>
                          <Typography variant="small" className="text-neutral-silver-gray">
                            First prorate charge on{" "}
                            {selectedPlan.trialEndsAt
                              ? new Date(selectedPlan.trialEndsAt).toLocaleDateString("en-AU", {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                })
                              : "TBD"}
                          </Typography>
                        </div>
                        <Typography variant="paragraph">{formatCurrency(selectedPlan.proratedAmount || 0, 0, "AUD")}</Typography>
                      </div>
                    </div>

                    {/* Total */}
                    <div className="border-t border-neutral-mid-gray pt-4">
                      <div className="flex justify-between">
                        <Typography variant="paragraph">Total:</Typography>
                        <Typography variant="h2">
                          {formatCurrency((selectedPlan.setupFee || 0) + (selectedPlan.proratedAmount || 0), 0, "AUD")}
                        </Typography>
                      </div>
                    </div>
                  </div>

                  {/* Subscribe Button */}
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="primary"
                      size="lg"
                      type="button"
                      className="w-full"
                      disabled={isSubmitting || !selectedPaymentMethodId || !selectedPlanId}
                      onClick={handleSubmit}
                    >
                      Subscribe and Continue
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : !loadingStripeData ? (
            <div className="rounded-lg border border-warning bg-warning/10 px-4 py-3 text-warning">
              <Typography variant="paragraph" className="text-warning">
                No Stripe customer configured for this provider. Please contact support.
              </Typography>
            </div>
          ) : null}
        </div>
      </div>

      {/* Cannot Downgrade Modal */}
      <CannotDowngradeModal
        open={cannotDowngradeModalOpen}
        onClose={() => setCannotDowngradeModalOpen(false)}
        onParentClose={onClose}
        data={downgradeModalData}
        title="Can't switch to this plan yet"
      />

      {/* Review Plan Changes Modal (Downgrade Step 1) */}
      <ReviewPlanChangesModal
        open={reviewPlanChangesModalOpen}
        onClose={() => setReviewPlanChangesModalOpen(false)}
        onContinue={handleReviewPlanChangesContinue}
        currentPlanId={currentSubscriptionId}
        newPlanId={targetPlanForModal?.subscriptionId}
        subscriptionData={plansData}
      />

      {/* Confirm Downgrade Modal (Downgrade Step 2) */}
      <ConfirmDowngradeModal
        open={confirmDowngradeModalOpen}
        onClose={() => setConfirmDowngradeModalOpen(false)}
        onConfirm={handleConfirmDowngrade}
        targetPlan={targetPlanForModal ?? undefined}
        subscriptionData={plansData}
        isLoading={isSubmitting}
      />
    </Modal>
  );
};
